import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useState, forwardRef } from "react";
import { Camera, Home as HomeIcon, ListChecks, FolderKanban, Download, Settings as CogIcon, Sparkles, HardHat, LogOut, ChevronDown } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import ChatFab from "@/components/ChatFab";
import AddToHomeScreenChip from "@/components/AddToHomeScreenChip";
import ErrorBoundary from "@/components/ErrorBoundary";
import AlertsBanner from "@/components/AlertsBanner";
import BuildIntelLogo from "@/components/BuildIntelLogo";
import JobPickerSheet from "@/components/JobPickerSheet";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const NAV = [
  { to: "/", label: "Capture", icon: Camera, testid: "nav-capture" },
  { to: "/approval", label: "Queue", icon: ListChecks, testid: "nav-approval" },
  { to: "/history", label: "Ledger", icon: HomeIcon, testid: "nav-history" },
  { to: "/jobs", label: "Jobs", icon: FolderKanban, testid: "nav-jobs" },
  { to: "/tax", label: "Tax AI", icon: Sparkles, testid: "nav-tax" },
];

const MORE = [
  { to: "/export", label: "Export", icon: Download, testid: "nav-export" },
  { to: "/ohs", label: "Take 5", icon: HardHat, testid: "nav-ohs" },
  { to: "/settings", label: "Settings", icon: CogIcon, testid: "nav-settings" },
];

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/** Compute initials from a name/email. "Sam Wilson" → "SW"; "sam@x.com" → "S". */
function initialsFor(user) {
  const raw = (user?.name || user?.email || "").trim();
  if (!raw) return "?";
  // Prefer name words when present. Emails collapse to the first char.
  if (raw.includes("@")) return raw[0].toUpperCase();
  const parts = raw.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase().slice(0, 2) || "?";
}

/**
 * AvatarButton — round chip in the top-right that opens the main menu.
 * Shows the uploaded avatar image (via /api/files/{avatar_path}, cookie-auth
 * on same-origin so no extra token needed) when profile.avatar_path is set.
 * Falls back to a solid orange chip with the tradie's initials so brand-new
 * users don't just see a floating cog. Both states use identical size so
 * layout doesn't jump when the avatar loads / gets replaced from Settings.
 */
const AvatarButton = forwardRef(function AvatarButton({ user, profile, ...props }, ref) {
  const [broken, setBroken] = useState(false);
  const initials = initialsFor(user);
  const src = profile?.avatar_path && !broken
    ? `${API_BASE}/api/files/${profile.avatar_path}`
    : null;
  return (
    <button
      ref={ref}
      {...props}
      data-testid="more-menu-btn"
      className="w-10 h-10 rounded-full bi-surface flex items-center justify-center hover:brightness-110 active:scale-95 transition overflow-hidden ring-1 ring-white/10"
      aria-label="Open menu"
    >
      {src ? (
        <img
          data-testid="topbar-avatar-img"
          src={src}
          alt=""
          className="w-full h-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : (
        <span
          data-testid="topbar-avatar-initials"
          className="font-heading font-extrabold text-[13px] tracking-wide text-[#1F1F1F] bg-[#FF9500] w-full h-full flex items-center justify-center"
        >
          {initials}
        </span>
      )}
    </button>
  );
});

function JobSelector() {
  const { jobs, activeJobId } = useApp();
  const active = jobs.find((j) => j.id === activeJobId);
  return (
    <JobPickerSheet testIdPrefix="job">
      <button
        data-testid="job-selector-btn"
        className="flex items-center gap-2 px-3 h-10 rounded-full bi-surface hover:brightness-110 active:scale-95 transition"
      >
        <span className="text-xs text-white/60 uppercase font-heading tracking-wider">Job</span>
        <span className="text-sm text-white font-medium truncate max-w-[140px]">
          {active ? active.name : "Select…"}
        </span>
        <ChevronDown className="w-4 h-4 text-white/60" />
      </button>
    </JobPickerSheet>
  );
}

function TopBar() {
  const { logout, user } = useAuth();
  const { profile } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <header className="sticky top-0 z-30 bg-[#1F1F1F]/90 backdrop-blur-md border-b border-white/5">
      <div className="max-w-2xl mx-auto px-4 py-2.5 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 shrink-0 group" data-testid="brand-link">
          <BuildIntelLogo size={30} testId="brand-logo" />
          <div>
            <div className="font-heading font-black text-white leading-none tracking-tight text-[15px] whitespace-nowrap">
              BUILDINTEL <span className="text-[#FF9500] drop-shadow-[0_0_8px_rgba(255,149,0,0.5)]">AI</span>
            </div>
            <div className="text-[10px] text-white/45 leading-none mt-1 font-normal tracking-wide whitespace-nowrap">
              Accounting in your pocket.
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <JobSelector />
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <AvatarButton user={user} profile={profile} />
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#1F1F1F] border-white/10 text-white w-72">
              <SheetHeader>
                <SheetTitle className="font-heading">{user?.name || user?.email}</SheetTitle>
                <SheetDescription className="text-white/50 sr-only">Main navigation menu</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-2">
                {[...NAV, ...MORE].map((n) => (
                  <Link key={n.to} to={n.to} onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 h-14 rounded-xl bi-surface hover:brightness-110" data-testid={`menu-${n.testid}`}>
                    <n.icon className="w-5 h-5 text-[#FF9500]" />
                    <span>{n.label}</span>
                  </Link>
                ))}
                <button data-testid="logout-btn" onClick={logout} className="w-full flex items-center gap-3 px-3 h-14 rounded-xl bi-ghost-btn">
                  <LogOut className="w-5 h-5 text-red-400" />
                  <span>Log out</span>
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function BottomNav() {
  const location = useLocation();
  return (
    <nav
      data-testid="bottom-nav"
      className="fixed inset-x-0 bottom-0 z-40 bg-[#1F1F1F]/95 backdrop-blur-md border-t border-white/[0.08]"
      style={{
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        // Force a GPU layer so iOS Safari (standalone PWA mode in
        // particular) keeps the bar pinned during momentum-scroll and
        // pull-to-refresh bounce. Without this, iOS re-lays out fixed
        // children mid-scroll and the bar visibly detaches.
        transform: "translateZ(0)",
        WebkitTransform: "translateZ(0)",
        willChange: "transform",
      }}
    >
      <div className="max-w-2xl mx-auto grid grid-cols-5 px-2 py-2">
        {NAV.map((n) => {
          const active = n.to === "/" ? location.pathname === "/" : location.pathname.startsWith(n.to);
          return (
            <Link key={n.to} to={n.to} data-testid={n.testid}
              className={`relative flex flex-col items-center justify-center gap-1 h-14 rounded-xl transition ${active ? "text-[#FF9500]" : "text-white/55 hover:text-white active:scale-95"}`}
            >
              {active && (
                <span
                  data-testid={`${n.testid}-underline`}
                  className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-1 rounded-full bg-[#FF9500] shadow-[0_0_10px_rgba(255,149,0,0.6)]"
                />
              )}
              <n.icon className={`w-5 h-5 transition-transform ${active ? "scale-110" : ""}`} strokeWidth={active ? 2.4 : 2} />
              <span className={`text-[10px] tracking-wide ${active ? "font-bold" : "font-medium"}`}>{n.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export default function Layout() {
  return (
    <div className="min-h-[100dvh] relative">
      {/* Each shell region gets its own boundary so a crash in TopBar's
          avatar chip or in ChatFab's LLM stream can't take out the
          navigation the tradie needs to escape to another tab. */}
      <ErrorBoundary>
        <TopBar />
      </ErrorBoundary>
      {/* Bottom padding is driven by the global --content-pad-bottom var
          (index.css :root) which itself derives from --fab-bottom. Any
          page that lifts --fab-bottom higher automatically gets extra
          list clearance so the last card scrolls fully above the FAB. */}
      <main
        className="max-w-2xl mx-auto px-4 py-4 relative z-10"
        style={{ paddingBottom: "var(--content-pad-bottom)" }}
      >
        <AlertsBanner />
        {/* Render-prop form: on Reload the boundary bumps `resetKey` which
            we forward to <Outlet />'s `key`, forcing React to unmount +
            remount the routed page. Cached bad state inside the page
            (hooks, refs, in-flight promises) is torn down so the crash
            doesn't instantly reappear. */}
        <ErrorBoundary>
          {(resetKey) => <Outlet key={resetKey} />}
        </ErrorBoundary>
      </main>
      <ErrorBoundary>
        <ChatFab />
      </ErrorBoundary>
      <ErrorBoundary>
        <AddToHomeScreenChip />
      </ErrorBoundary>
      <ErrorBoundary>
        <BottomNav />
      </ErrorBoundary>
    </div>
  );
}
